"use client"

import { Check } from '@/app/components/Icons/Phosphoricons';

export default function ContentBTS() {
    return (
        <main
            className="bg-white px-6 py-32 lg:px-8"
            role="main"
            aria-labelledby="page-title"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"' }}
        >
            {/* Hero / Intro Section */}
            <div className="mx-auto max-w-3xl text-base leading-7 text-gray-700">
                <p className="text-base font-semibold text-blue-600">
                    Business Technology Tools
                </p>
                <h1
                    id="page-title"
                    className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                >
                    Optimize Operations with Scalable Business Solutions
                </h1>
                <p className="mt-6 text-xl leading-8">
                    Empower your business with cutting edge technology solutions
                    designed for automation, communication, and efficiency.
                </p>

                {/* Content Overview Section */}
                <div className="mt-10 max-w-2xl">
                    <p>
                        In today's rapidly evolving business landscape, technology integration is no longer optional but essential for sustainable growth.
                        Our comprehensive suite of business technology solutions provides the foundation for operational excellence,
                        enabling organizations of all sizes to streamline processes, enhance customer engagement, and drive strategic decision making.
                    </p>

                    <ul role="list" className="mt-8 max-w-xl space-y-8 text-gray-600">
                        <li className="flex gap-x-3">
                            <Check
                                aria-hidden="true"
                                className="mt-1 h-5 w-5 flex-none text-blue-600"
                            />
                            <span>
                              <strong className="font-semibold text-gray-900">Intelligent Automation.</strong>{' '}
                                Reduce manual workloads by implementing smart
                              process automation, enabling teams to focus on
                              strategic initiatives and creative problem solving.
                            </span>
                        </li>
                        <li className="flex gap-x-3">
                            <Check
                                aria-hidden="true"
                                className="mt-1 h-5 w-5 flex-none text-blue-600"
                            />
                            <span>
                              <strong className="font-semibold text-gray-900">Smart Digital Assistants.</strong>{' '}
                                Leverage advanced virtual assistants to handle routine tasks,
                              provide consistent customer support, and automate complex decision making processes.
                            </span>
                        </li>
                        <li className="flex gap-x-3">
                            <Check
                                aria-hidden="true"
                                className="mt-1 h-5 w-5 flex-none text-blue-600"
                            />
                            <span>
                              <strong className="font-semibold text-gray-900">Modern Communication Solutions.</strong>{' '}
                                Secure and scalable digital communication powered by
                              enterprise grade cloud infrastructure for seamless business interactions.
                            </span>
                        </li>
                        <li className="flex gap-x-3">
                            <Check
                                aria-hidden="true"
                                className="mt-1 h-5 w-5 flex-none text-blue-600"
                            />
                            <span>
                              <strong className="font-semibold text-gray-900">Enterprise Voice Systems.</strong>{' '}
                                Cloud based business telephony provides reliable, scalable
                              voice communication with advanced features for organizations of all sizes.
                            </span>
                        </li>
                        <li className="flex gap-x-3">
                            <Check
                                aria-hidden="true"
                                className="mt-1 h-5 w-5 flex-none text-blue-600"
                            />
                            <span>
                              <strong className="font-semibold text-gray-900">Real Time Collaboration Tools.</strong>{' '}
                                Modern messaging platforms enhance customer engagement
                              and internal team collaboration across departments and locations.
                            </span>
                        </li>
                        <li className="flex gap-x-3">
                            <Check
                                aria-hidden="true"
                                className="mt-1 h-5 w-5 flex-none text-blue-600"
                            />
                            <span>
                              <strong className="font-semibold text-gray-900">Strategic Technology Consulting.</strong>{' '}
                                Expert guidance to optimize technology adoption and streamline
                              digital transformation journeys with minimal disruption.
                            </span>
                        </li>
                    </ul>

                    <p className="mt-8">
                        Our integrated solutions ensure businesses remain competitive in a digital first world,
                        improving stakeholder communication, reducing operational costs, and enhancing overall customer experiences.
                        We take a holistic approach to business technology, addressing not just immediate needs but creating
                        scalable foundations for future growth.
                    </p>

                    <h2 className="mt-16 text-2xl font-bold tracking-tight text-gray-900">
                        Scalable, Secure, and Future Proof Solutions
                    </h2>
                    <p className="mt-6">
                        Our technology ecosystem is architected for seamless scalability,
                        offering enterprise grade availability, advanced security protocols, and effortless integration
                        with your existing business systems. We design solutions that grow alongside your organization,
                        ensuring technology remains an enabler rather than a constraint.
                    </p>

                    <figure className="mt-10 border-l border-blue-600 pl-9" aria-labelledby="quote-title">
                        <blockquote className="font-semibold text-gray-900">
                            <p id="quote-title">
                                "By implementing smart automation and cloud communication tools,
                                we significantly reduced operational costs while simultaneously driving substantial revenue growth."
                            </p>
                        </blockquote>
                    </figure>
                </div>

                {/* Enhanced Description Section */}
                <div className="mt-16 bg-gray-50 p-8 rounded-xl shadow-sm">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                        Comprehensive Business Technology Ecosystem
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="text-lg font-semibold text-blue-600 mb-2">
                                Smart Process Automation
                            </h4>
                            <p className="text-gray-700">
                                Our advanced workflow automation solutions streamline repetitive tasks
                                and integrate seamlessly with existing business systems, creating operational efficiencies
                                while maintaining governance and oversight.
                            </p>
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-blue-600 mb-2">
                                Enterprise Communication Suite
                            </h4>
                            <p className="text-gray-700">
                                Unified communication platforms combine digital messaging, voice, and collaboration tools
                                with enterprise grade security, enabling consistent communication experiences.
                            </p>
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-blue-600 mb-2">
                                Data Driven Decision Making
                            </h4>
                            <p className="text-gray-700">
                                Transform raw business information into actionable insights with our
                                comprehensive analytics and reporting tools, enabling more informed strategic decisions.
                            </p>
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold text-blue-600 mb-2">
                                Implementation & Support
                            </h4>
                            <p className="text-gray-700">
                                Our dedicated implementation specialists ensure smooth transitions to new technologies,
                                with comprehensive training programs and ongoing technical support services.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Future Ready Section */}
                <div className="mt-16 max-w-2xl">
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                        A Future Ready Business Technology Stack
                    </h2>
                    <p className="mt-6">
                        Modern enterprises require agile, scalable, and intelligent tools
                        to maintain competitive advantage. Our solutions incorporate the latest advancements
                        in automation, advanced analytics, and cloud infrastructure to future proof your business operations.
                    </p>

                    <div className="mt-8 space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Modern Cloud Architecture</h3>
                            <p className="mt-2 text-gray-600">
                                Our technology foundation is built on modern cloud principles, providing unmatched
                                scalability, reliability, and cost efficiency. Leverage containerized applications,
                                modular services, and serverless computing to adapt quickly to evolving business requirements.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Advanced Security Framework</h3>
                            <p className="mt-2 text-gray-600">
                                Protect critical business assets with enterprise grade security features including
                                comprehensive encryption, advanced authentication methods, and automated threat detection.
                                Our solutions adhere to global security standards while maintaining intuitive user experiences.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Seamless Integration Capabilities</h3>
                            <p className="mt-2 text-gray-600">
                                Connect and extend your technology ecosystem with our comprehensive integration framework.
                                Effortlessly connect with existing business systems, third party services, and
                                emerging technologies to create a unified operational environment.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Intelligent Decision Support</h3>
                            <p className="mt-2 text-gray-600">
                                Transform decision making processes with advanced analytics, natural language
                                processing, and machine learning algorithms that continuously learn from your business data.
                                Gain actionable insights and automate complex workflows with confidence and precision.
                            </p>
                        </div>
                    </div>

                    <p className="mt-8 text-gray-700 border-t border-gray-200 pt-8">
                        Partner with us to build a resilient technology foundation that adapts to evolving market
                        conditions and supports your long term business objectives. Our technology stack doesn&apos;t just
                        address today&apos;s challenges—it anticipates tomorrow&apos;s opportunities, positioning your
                        organization for sustainable success in an increasingly digital business landscape.
                    </p>
                </div>
            </div>
        </main>
    )
}